import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface QueueLocation {
  id: string;
  name: string;
  primary_type: string | null;
  ownership: string;
  source?: string | null;
  is_rejected: boolean;
  classifications: {
    has_sport_field: boolean | null;
    user_has_sport_field: boolean | null;
    confidence?: number | null;
    source: string;
  }[];
}

const sourceStyles: Record<string, { label: string; className: string }> = {
  equip_scout: {
    label: "Equip Scout",
    className: "text-indigo-700 bg-indigo-100",
  },
  city: { label: "City", className: "text-emerald-700 bg-emerald-100" },
  external: { label: "External", className: "text-amber-700 bg-amber-100" },
};

function getSourceStyle(source: string | null | undefined) {
  if (!source) return sourceStyles.external;
  return (
    sourceStyles[source] ?? {
      label: source.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      className: "text-gray-700 bg-gray-100",
    }
  );
}

interface ReviewQueueProps {
  locations: QueueLocation[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function formatType(type: string | null): string {
  if (!type) return "Unknown type";
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ReviewQueue({
  locations,
  selectedId,
  onSelect,
}: ReviewQueueProps) {
  const [search, setSearch] = useState("");

  if (locations.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm p-4">
        No locations to review.
      </div>
    );
  }

  const filtered = search
    ? locations.filter((loc) =>
        loc.name.toLowerCase().includes(search.toLowerCase()),
      )
    : locations;

  return (
    <div className="flex flex-col h-full">
      <div className="p-2 border-b shrink-0">
        <Input
          placeholder="Search locations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 text-sm"
        />
      </div>
      <ScrollArea className="flex-1">
        {filtered.length === 0 ? (
          <div className="flex items-center justify-center h-20 text-muted-foreground text-sm">
            No matches found.
          </div>
        ) : (
          <div className="space-y-1 p-2">
            {filtered.map((loc) => {
              const isSelected = loc.id === selectedId;

              return (
                <button
                  key={loc.id}
                  onClick={() => onSelect(loc.id)}
                  className={cn(
                    "w-full text-left rounded-lg border p-3 transition-colors",
                    isSelected
                      ? "border-primary bg-accent/10"
                      : "border-transparent hover:bg-muted",
                    loc.is_rejected && "opacity-50",
                  )}
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <p className="text-sm font-medium truncate">{loc.name}</p>
                    {(() => {
                      const s = getSourceStyle(loc.source);
                      return (
                        <span
                          className={`inline-block rounded-full px-1.5 py-0 text-[10px] font-medium shrink-0 ${s?.className}`}
                        >
                          {s?.label}
                        </span>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {formatType(loc.primary_type)}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
